import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Filter } from '@docpath/linces-angular-components/common';
import { DpFieldButton, DpFormModule, DpFormNavigationService, DpModalService, DpSectionForm, Table } from '@docpath/webcomponents';
import { Subscription } from 'rxjs';

import { FailedJobsHeaderComponent } from './components/header/failed-jobs-header.component';
import { FailedJobsTableComponent } from './components/table/failed-jobs-table.component';
import { FailedJobsTableService } from './components/table/failed-jobs-table.service';
import { Notification } from './models/notification';
import { FailedJobsService } from './services/failed-jobs.service';
import { NotificationsConfigFormService } from './services/notifications-config-form.service';
import { NotificationsConfigHttpService } from './services/notifications-config-http.service';

@Component({
  selector: 'app-failed-jobs',
  templateUrl: './failed-jobs.component.html',
  standalone: true,
  imports: [DpFormModule, FailedJobsHeaderComponent, FailedJobsTableComponent]
})
export class FailedJobsComponent implements OnInit, OnDestroy {
  sectionForm: DpSectionForm;
  filters: Filter[] = [];
  selectedNotifications: Notification[] = [];

  private table?: Table;
  private indexServicesChangedSubscription?: Subscription;

  private readonly failedJobsService = inject(FailedJobsService);
  private readonly failedJobsTableService = inject(FailedJobsTableService);
  private readonly notificationsConfigFormService = inject(NotificationsConfigFormService);
  private readonly notificationsConfigHttpService = inject(NotificationsConfigHttpService);
  private readonly formNavigationService = inject(DpFormNavigationService);
  private readonly modalService = inject(DpModalService);

  constructor() {
    this.sectionForm = this.notificationsConfigFormService.getSectionForm();
    this.initUpdateButton();
  }

  ngOnInit(): void {
    if (!this.indexServicesChangedSubscription) {
      this.indexServicesChangedSubscription = this.failedJobsService.failedJobsChanged.subscribe(() => this.refreshNotifications());
    }

    this.failedJobsTableService.failedJobsLoaded.subscribe(result => {
      const config = { 'checkInterval': result.checkInterval };
      this.sectionForm.setValue({ config });
    });
  }

  ngOnDestroy(): void {
    if (this.indexServicesChangedSubscription) {
      this.indexServicesChangedSubscription.unsubscribe();
    }
  }

  getTable(): Table | undefined {
    return this.table;
  }

  onTableLoaded(table: Table): void {
    this.table = table;
  }

  onFilterChange(filters: Filter[]): void {
    this.filters = filters;
  }

  onCheckChange(): void {
    if (this.table) {
      this.selectedNotifications = this.failedJobsTableService.getSelectedNotifications(this.table);
    }
  }

  getSelectedNotificationCount(): number {
    if (this.table) {
      const notifications = this.failedJobsTableService.getSelectedNotifications(this.table);

      return notifications.length;
    }

    return 0;
  }

  getSelectedNotification(): Notification | undefined {
    if (this.table) {
      const notifications = this.failedJobsTableService.getSelectedNotifications(this.table);

      return notifications.length === 1 ? notifications[0] : undefined;
    }

    return undefined;
  }

  private initUpdateButton() {
    const updateButtonField = this.sectionForm.getSectionField('config', 'update');

    if (updateButtonField) {
      (updateButtonField as DpFieldButton).click = () => {
        const checkInterval = this.sectionForm.getFieldValue('checkInterval');
        if (!checkInterval) {
          return;
        }

        const checkIntervalNumber = Number(checkInterval);
        if (checkIntervalNumber < 5 || checkIntervalNumber > 9999) {
          this.modalService.openError('failed-jobs.modal.check-interval-out-of-range');

          return;
        }

        this.notificationsConfigHttpService.updateConfig(checkInterval as number).subscribe(() => {
          this.sectionForm.setValue(checkInterval);
          this.formNavigationService.setUnsavedChanges(false);
        });
      };
    }
  }

  private refreshNotifications(): void {
    this.failedJobsTableService.loadNotifications(this.filters, 1);
  }

}

